import { type ComponentProps } from 'react';

import { SandboxLink } from '@/components/sandboxLink';
import { type ItemType } from '@/models/item/item';
import { PagePermissions } from '@/models/pagePermissions';

type CatalogType = Exclude<ItemType, 'season_pass'>;

const PAGE_PERMISSIONS = {
  item: PagePermissions.catalogItems,
  bundle: PagePermissions.catalogBundles,
  currency: PagePermissions.catalogCurrency,
} satisfies Record<CatalogType, unknown>;

const isCatalogType = (type: string): type is CatalogType =>
  Object.prototype.hasOwnProperty.call(PAGE_PERMISSIONS, type);

/** The catalog page that opens this item, relative to the sandbox. */
export const itemPath = (type: CatalogType, itemId: string) =>
  `items/${type}?show=${itemId}`;

type ItemLinkProps = Omit<
  ComponentProps<typeof SandboxLink>,
  'to' | 'oneOf'
> & {
  itemId: string;
  /** Plain string: several API shapes type it loosely. Anything but a catalog type stays plain. */
  type: ItemType | (string & {});
};

/**
 * Links whatever names an item to its catalog sheet. A season pass's own page
 * is keyed by season_pass_id, not its backing item_id, so one stays plain here.
 */
export const ItemLink = ({
  itemId,
  type,
  children,
  ...props
}: ItemLinkProps) =>
  !isCatalogType(type) ? (
    children
  ) : (
    <SandboxLink
      to={itemPath(type, itemId)}
      oneOf={PAGE_PERMISSIONS[type]}
      {...props}
    >
      {children}
    </SandboxLink>
  );
